import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import { RankBadge } from '@/utils/ranks';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { Save, Upload, Globe } from 'lucide-react';

export default function Settings() {
  const { user, loading: authLoading, updateUser } = useAuth();
  const { t, lang } = useLang();
  const toast = useGravityToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: '', username: '', bio: '' });
  const [emailLang, setEmailLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [savingLang, setSavingLang] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth');
    if (user) {
      setForm({ display_name: user.display_name || '', username: user.username || '', bio: user.bio || '' });
      setEmailLang(user.email_lang || 'en');
    }
  }, [user, authLoading, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/profiles/${user.username}`, form);
      updateUser(data);
      toast.success(t('settings.saved'));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleSaveEmailLang = async (newLang) => {
    setSavingLang(true);
    setEmailLang(newLang);
    try {
      await api.put('/auth/email-lang', { email_lang: newLang });
      updateUser({ email_lang: newLang });
      toast.success(lang === 'ar' ? 'تم حفظ لغة البريد' : 'Email language saved');
    } catch {
      toast.error('Failed to save');
    }
    setSavingLang(false);
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/profiles/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser({ avatar_url: data.url });
      toast.success('Avatar updated');
    } catch { toast.error('Failed to upload avatar'); }
  };

  if (authLoading || !user) return null;

  return (
    <div data-testid="settings-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-2xl mx-auto">
        <ScrollReveal>
          <h1 className="text-3xl font-display text-heading mb-8" data-testid="settings-title">{t('settings.title')}</h1>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <div className="glass-card rounded-2xl p-6 md:p-8 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative group">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-sacred-gold/10">
                  {user.avatar_url ? (
                    <img src={user.avatar_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : user.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-sacred-gold text-2xl font-display">
                      {user.display_name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Upload size={20} className="text-cream" />
                  <input type="file" accept="image/*" onChange={handleAvatar} className="hidden" data-testid="avatar-upload" />
                </label>
              </div>
              <div>
                <p className="text-deep-brown font-body font-medium">{user.display_name}</p>
                <RankBadge rank={user.rank || 'Seeker'} size="sm" />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-6 text-center">
              <div className="glass-card rounded-xl p-3">
                <p className="text-xs text-text-muted/70 font-body">{t('settings.rank')}</p>
                <p className="text-sacred-gold font-body font-semibold">{user.rank || 'Seeker'}</p>
              </div>
              <div className="glass-card rounded-xl p-3">
                <p className="text-xs text-text-muted/70 font-body">{t('settings.points')}</p>
                <p className="text-deep-brown font-body font-semibold">{user.rank_points || 0}</p>
              </div>
              <div className="glass-card rounded-xl p-3">
                <p className="text-xs text-text-muted/70 font-body">{t('profile.posts')}</p>
                <p className="text-deep-brown font-body font-semibold">{user.post_count || 0}</p>
              </div>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('settings.displayName')}</label>
                <input type="text" value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="settings-display-name" />
              </div>
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('settings.username')}</label>
                <input type="text" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm" data-testid="settings-username" />
              </div>
              <div>
                <label className="text-warm-brown text-xs font-body mb-1 block">{t('settings.bio')}</label>
                <textarea value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} rows={3} maxLength={500} className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm resize-none" data-testid="settings-bio" />
                <p className="text-text-muted/30 text-xs font-body mt-1">{form.bio.length}/500</p>
              </div>
              <button type="submit" disabled={saving} className="gold-button px-6 py-3 rounded-full font-body text-sm flex items-center gap-2 disabled:opacity-50" data-testid="settings-save">
                <Save size={16} /> {saving ? '...' : t('settings.save')}
              </button>
            </form>
          </div>
        </ScrollReveal>

        {/* Email Language Preference */}
        <ScrollReveal delay={0.2}>
          <div className="glass-card rounded-2xl p-6 md:p-8" data-testid="email-lang-section">
            <div className="flex items-center gap-3 mb-2">
              <Globe size={18} className="text-sacred-gold" />
              <h2 className="font-display text-heading text-lg">{lang === 'ar' ? 'لغة البريد الإلكتروني' : 'Email Language'}</h2>
            </div>
            <p className="text-text-muted text-sm font-body mb-4">
              {lang === 'ar'
                ? 'اختر اللغة التي تفضّل استلام الرسائل بها (النشرات، تأكيدات الطلبات، روابط الدخول).'
                : 'Choose the language for emails you receive from us (newsletters, order confirmations, sign-in links).'}
            </p>
            <div className="flex gap-3">
              {[{ v: 'en', label: 'English' }, { v: 'ar', label: 'العربية' }].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => handleSaveEmailLang(opt.v)}
                  disabled={savingLang}
                  className={`px-5 py-2.5 rounded-full font-body text-sm transition-all ${emailLang === opt.v ? 'gold-button' : 'glass-button text-text-muted hover:text-heading'}`}
                  data-testid={`email-lang-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
}
